import api from '../utils/api';

/**
 * Authentication service for handling user authentication with the WASTENOT API
 */
const authService = {
  /**
   * Register a new user
   * @param {Object} userData - User registration data
   * @returns {Promise} - API response
   */
  register: async (userData) => {
    try {
      const response = await api.post('/auth/register', userData);
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Registration failed' };
    }
  },

  /**
   * Login a user and store token and user info
   * @param {Object} credentials - User credentials
   * @returns {Promise} - API response with user data and token
   */
  login: async (credentials) => {
    try {
      const response = await api.post('/auth/login', credentials);
      const { token, user } = response.data;
      
      // Store token and user data in localStorage
      localStorage.setItem('wastenet_token', token);
      localStorage.setItem('wastenet_user', JSON.stringify(user));
      
      return { user, token };
    } catch (error) {
      throw error.response?.data || { message: 'Login failed' };
    }
  },

  /**
   * Logout the current user
   */
  logout: () => {
    localStorage.removeItem('wastenet_token');
    localStorage.removeItem('wastenet_user');
    window.location.href = '/login';
  },

  /**
   * Get the current user profile
   * @returns {Promise} - API response with user profile data
   */
  getProfile: async () => {
    try {
      const response = await api.get('/auth/profile');
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Failed to fetch profile' };
    }
  },

  /**
   * Update user profile
   * @param {Object} profileData - Updated profile data
   * @returns {Promise} - API response
   */
  updateProfile: async (profileData) => {
    try {
      const response = await api.put('/auth/profile', profileData);
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Failed to update profile' };
    }
  },

  /**
   * Change user password
   * @param {Object} passwordData - Old and new password data
   * @returns {Promise} - API response
   */
  changePassword: async (passwordData) => {
    try {
      const response = await api.post('/auth/change-password', passwordData);
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Failed to change password' };
    }
  },

  /**
   * Check if user is authenticated
   * @returns {Boolean} - Authentication status
   */
  isAuthenticated: () => {
    return !!localStorage.getItem('wastenet_token');
  },

  /**
   * Get the current user data
   * @returns {Object|null} - User data or null if not logged in
   */
  getCurrentUser: () => {
    const userJson = localStorage.getItem('wastenet_user');
    return userJson ? JSON.parse(userJson) : null;
  },
};

export default authService;
